import { useContext, useState, useEffect, useRef } from "react";
import { Slider } from "primereact/slider";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { AddPropertyContext } from "./AddPropertyContext";
import { REIDialoge } from "../../../components/ui";
import StampDutyCalculator from "./StampDutyCalculator";
import { formatCurrency } from "../../../utils/commonUtil";
import { IUserProfileDetails } from "../../../interface/userdetails";

interface OtherPurchaseExpensesProps {
  isEditMode?: boolean;
  isUserDefault?: boolean;
}

const OtherPurchaseExpenses = (props: OtherPurchaseExpensesProps) => {
  const context = useContext(AddPropertyContext);
  const [showCalculateStampDuty, setShowCalculateStampDuty] = useState(false);
  const isInitializedRef = useRef(false);

  if (!context) {
    return <div>Loading...</div>;
  }

  const { property, setProperty } = context;

  useEffect(() => {
    if (!(property as any).PurchaseModifiedFields && !isInitializedRef.current) {
      setProperty((prev) => ({
        ...prev,
        PurchaseModifiedFields: {
          Cost_SolicitorFees: false,
          Cost_CapitalImprovements: false,
          Cost_StampDuty: false,
          Cost_OtherCosts: false,
        },
      }));
      isInitializedRef.current = true;
    }
  }, []);

  useEffect(() => {
    if (props.isEditMode) return;
    if (!context.userProfile) return;
    if (!isInitializedRef.current) return;

    const modifiedFields: Record<string, boolean> = (property as any).PurchaseModifiedFields || {};

    const profileCosts = (context.userProfile as IUserProfileDetails) || {};

    const next: any = { ...property };

    const setIfUnmodified = (field: string, value: any) => {
      if (!modifiedFields[field] && value !== undefined && value !== null) {
        next[field] = Number(value);
      }
    };
    setIfUnmodified("Cost_CapitalImprovements", profileCosts.LoanCost.ImprovementFee);
    setIfUnmodified("Cost_SolicitorFees", profileCosts.LoanCost.SolicitorFee);

    setProperty({ ...next, PurchaseModifiedFields: { ...modifiedFields } });
  }, [props.isUserDefault, context?.userProfile]);

  const handleInputChange = (field: keyof typeof property, value: any) => {
    const modified: Record<string, boolean> = (property as any).PurchaseModifiedFields || {};

    setProperty({
      ...property,
      [field]: value,
      PurchaseModifiedFields: { ...modified, [field]: true },
    });
  };

  const hideStampDutyDialog = () => {
    setShowCalculateStampDuty(false);
  };

  return (
    <>
      <REIDialoge
        showHeader={true}
        header={`Stamp duty calculator`}
        visible={showCalculateStampDuty}
        handleEditClick={() => hideStampDutyDialog()}
        position="top"
        customClass="update-statistics-dialog"
        style={{ width: "40vw" }}
        contentComponent={<StampDutyCalculator closeDialogue={hideStampDutyDialog} />}
      ></REIDialoge>

      <div className="relative rounded-lg p-4 border-solid border border-gray-300 bg-white mb-4 mt-4">
        <div className="default-profile-header-container">
          <h3 className="text-[var(--primary-color)] mt-0 m-1 text-lg">Other purchase expenses</h3>
        </div>
        <div className="flex flex-row gap-4 mb-4">
          <div className="flex-0 flex-col w-1/2 gap-2">
            <div className="flex items-center">
              <label htmlFor="capitalImprovements" className="w-1/4 mr-14">
                Capital improvements at purchase ($)
              </label>
              {!props.isEditMode && (
                <>
                  <Slider
                    id="apope-sldr-capitalimprovements"
                    data-testid="apope-sldr-capitalimprovements"
                    value={
                      (property.Cost_CapitalImprovements || 0) >= 1500000
                        ? 1500000
                        : property.Cost_CapitalImprovements || 0
                    }
                    onChange={(e: any) => handleInputChange("Cost_CapitalImprovements", e.value)}
                    className="w-2/4 px-4"
                    max={1500000}
                  />
                  <InputNumber
                    inputId="capitalImprovements"
                    data-testid="apope-num-capitalimprovements"
                    value={property.Cost_CapitalImprovements || 0}
                    onValueChange={(e) => handleInputChange("Cost_CapitalImprovements", e.value!)}
                    className="w-1/4 ml-3"
                  />
                </>
              )}
              {props.isEditMode && (
                <label className="w-1/4">{formatCurrency(property.PurchaseCost_TotalCapitalImprovements || 0)}</label>
              )}
            </div>
          </div>
          <div className="flex-0 flex-col w-1/2 gap-2" hidden={context.property?.CountryCode?.toString() === "2"}>
            <div className="flex items-center">
              <div className="flex w-1/4">
                <label htmlFor="stampDuty">Stamp duty ($)</label>
                {!props.isEditMode && (
                  <Button
                    id="apope-btn-calculatestampduty"
                    data-testid="apope-btn-calculatestampduty"
                    label="Calculate"
                    className="p-button-link"
                    onClick={() => setShowCalculateStampDuty(true)}
                  />
                )}
              </div>
              {!props.isEditMode && (
                <>
                  <Slider
                    id="apope-sldr-stampduty"
                    data-testid="apope-sldr-stampduty"
                    value={(property.Cost_StampDuty || 0) >= 100000 ? 100000 : property.Cost_StampDuty || 0}
                    onChange={(e: any) => handleInputChange("Cost_StampDuty", e.value)}
                    className="w-2/4 px-4"
                    max={100000}
                  />
                  <InputNumber
                    inputId="stampDuty"
                    data-testid="apope-num-stampduty"
                    value={property.Cost_StampDuty || 0}
                    onValueChange={(e) => handleInputChange("Cost_StampDuty", e.value!)}
                    className="w-1/4 ml-3"
                    maxFractionDigits={0}
                    minFractionDigits={0}
                  />
                </>
              )}
              {props.isEditMode && (
                <label className="w-1/4">{formatCurrency(property.PurchaseCost_TotalStampDuty || 0)}</label>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-row gap-4 mb-4">
          <div className="flex-0 flex-col w-1/2 gap-2">
            <div className="flex items-center">
              <label htmlFor="solicitorsCosts" className="w-1/4 mr-14">
                Solicitors costs ($)
              </label>
              {!props.isEditMode && (
                <>
                  <Slider
                    id="apope-sldr-solicitorscosts"
                    data-testid="apope-sldr-solicitorscosts"
                    value={(property.Cost_SolicitorFees || 0) >= 50000 ? 50000 : property.Cost_SolicitorFees || 0}
                    onChange={(e: any) => handleInputChange("Cost_SolicitorFees", e.value)}
                    className="w-2/3 px-4"
                    max={50000}
                  />
                  <InputNumber
                    inputId="solicitorsCosts"
                    data-testid="apope-num-solicitorscosts"
                    value={property.Cost_SolicitorFees || 0}
                    onValueChange={(e) => handleInputChange("Cost_SolicitorFees", e.value!)}
                    className="w-1/3 ml-3"
                  />
                </>
              )}
              {props.isEditMode && (
                <label className="w-1/4">{formatCurrency(property.PurchaseCost_TotalSolicitorFees || 0)}</label>
              )}
            </div>
          </div>
          <div className="flex-0 flex-col w-1/2 gap-2">
            <div className="flex items-center">
              <label htmlFor="otherCosts" className="w-1/4">
                Other costs ($)
              </label>
              {!props.isEditMode && (
                <>
                  <Slider
                    id="apope-sldr-othercosts"
                    data-testid="apope-sldr-othercosts"
                    value={(property.Cost_OtherCosts || 0) >= 50000 ? 50000 : property.Cost_OtherCosts || 0}
                    onChange={(e: any) => handleInputChange("Cost_OtherCosts", e.value)}
                    className="w-2/4 px-4"
                    max={50000}
                  />
                  <InputNumber
                    inputId="otherCosts"
                    data-testid="apope-num-othercosts"
                    value={property.Cost_OtherCosts || 0}
                    onValueChange={(e) => handleInputChange("Cost_OtherCosts", e.value!)}
                    className="w-1/4 ml-3"
                  />
                </>
              )}
              {props.isEditMode && (
                <label className="w-1/4">{formatCurrency(property.PurchaseCost_TotalOtherCosts || 0)}</label>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OtherPurchaseExpenses;
